import React, { useMemo } from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { useParams } from "react-router";
import { Page, PageNotFound, QueryResult } from "@sscs/ui";
import { getQueryStatus } from "@sscs/utils";
import { useAppSelector } from "Hooks";
import { useGetWhoamiQuery } from "Services";
import { selectDeletedProjects } from "Store/projects.slice";
import { useGetProjectQuery } from "../../services/projects.api";
import PackagesList from "./PackagesList";
import ProjectDeleted from "./ProjectDeleted";

const ProjectsContent = (): React.JSX.Element | null => {
  const { data: whoami } = useGetWhoamiQuery();
  const deletedProjects = useAppSelector(selectDeletedProjects);
  const { projectId } = useParams();

  const {
    data: project,
    isFetching,
    isLoading,
    isError,
    error,
  } = useGetProjectQuery(
    projectId
      ? {
          projectId: projectId,
        }
      : skipToken,
  );
  const isDeleted = useMemo(() => {
    if (project && deletedProjects.length > 0) {
      return deletedProjects.some(
        (item) =>
          item.id === project.project_id && item.initiated_by !== whoami?.email,
      );
    } else {
      return false;
    }
  }, [project, deletedProjects, whoami]);

  if (isFetching || isLoading) {
    return <Page.Body status={{ status: "LOADING" }} />;
  } else if (isError || !project) {
    return (
      <PageNotFound
        title="We can't find that project"
        byline="Try selecting a project from the sidebar"
      />
    );
  }

  return (
    <>
      <QueryResult
        status={getQueryStatus({
          isLoading,
          isFetching,
          isError,
          error,
          data: project,
        })}
        disableFetching
      >
        <PackagesList project={project} />
      </QueryResult>
      <ProjectDeleted isOpen={isDeleted} />
    </>
  );
};

export default ProjectsContent;
